import React, { useState } from "react";
import { FaTimes, FaRupeeSign, FaCreditCard, FaMobile } from "react-icons/fa";

const CommissionPaymentModal = ({
  isOpen,
  onClose,
  onSubmit,
  commissionAmount,
}) => {
  const [paymentData, setPaymentData] = useState({
    amount: commissionAmount,
    paymentMode: "upi",
    referenceId: "",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!paymentData.amount || paymentData.amount <= 0) {
      newErrors.amount = "Please enter a valid amount";
    } else if (parseFloat(paymentData.amount) > commissionAmount) {
      newErrors.amount = "Amount cannot exceed outstanding commission";
    }

    if (!paymentData.referenceId.trim()) {
      newErrors.referenceId = "Transaction reference ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({
      amount: parseFloat(paymentData.amount),
      paymentMode: paymentData.paymentMode,
      referenceId: paymentData.referenceId.trim(),
    });

    // Reset form
    setPaymentData({
      amount: commissionAmount,
      paymentMode: "upi",
      referenceId: "",
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setPaymentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Update amount when commissionAmount changes
  React.useEffect(() => {
    setPaymentData((prev) => ({
      ...prev,
      amount: commissionAmount,
    }));
  }, [commissionAmount]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">Pay Commission</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <FaRupeeSign className="w-5 h-5 text-red-600 mr-2" />
                <span className="font-medium text-red-800">
                  Outstanding Commission
                </span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(commissionAmount)}
              </p>
              <p className="text-sm text-red-700 mt-1">
                This amount is due to the company for cash rides
              </p>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">₹</span>
              <input
                type="number"
                value={paymentData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.amount ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter amount"
                min="1"
                max={commissionAmount}
              />
            </div>
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              You can pay partial amount or full commission
            </p>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Payment Method *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange("paymentMode", "upi")}
                className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                  paymentData.paymentMode === "upi"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <FaMobile className="w-6 h-6 mb-2 text-purple-600" />
                <span className="text-sm font-medium">UPI</span>
              </button>
              <button
                type="button"
                onClick={() => handleInputChange("paymentMode", "online")}
                className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                  paymentData.paymentMode === "online"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <FaCreditCard className="w-6 h-6 mb-2 text-blue-600" />
                <span className="text-sm font-medium">Online</span>
              </button>
            </div>
          </div>

          {/* Transaction Reference ID */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Reference ID *
            </label>
            <input
              type="text"
              value={paymentData.referenceId}
              onChange={(e) => handleInputChange("referenceId", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.referenceId ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Enter transaction ID from your payment"
            />
            {errors.referenceId && (
              <p className="text-red-500 text-sm mt-1">{errors.referenceId}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Please provide the transaction ID after making the payment
            </p>
          </div>

          {/* Payment Instructions */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">
              Payment Instructions:
            </h4>
            <ol className="text-sm text-yellow-700 space-y-1">
              <li>
                1. Make payment to company account via UPI or online transfer
              </li>
              <li>2. Note down the transaction reference ID</li>
              <li>3. Enter the reference ID above and submit</li>
              <li>4. Your wallet balance will be updated after verification</li>
            </ol>
          </div>
        </div>

        {/* Submit Buttons - Fixed at bottom */}
        <form onSubmit={handleSubmit} className="p-6 border-t bg-gray-50 flex-shrink-0">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Submit Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommissionPaymentModal;
